import { useCallback, useEffect, useState } from 'react';
import { Route, Switch } from 'react-router';
import { ChannelGroupForm } from './ChannelGroupForm/ChannelGroupForm';
import { ChannelGroupList } from './ChannelGroupList/ChannelGroupList';
import { ChannelGroupDetail } from './ChannelGroupDetail/ChannelGroupDetail';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import {
  ChannelGroupListDTO,
  ChannelGroupStatusFilter
} from 'core/channelGroup/ChannelGroup';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useChannelGroupEditFormModel } from './ChannelGroupForm/ChannelGroupFormModel';

const channelGroupManager: ChannelGroupManager =
  new DefaultChannelGroupManager();

export const ChannelGroups: React.FC = () => {
  const { loading, callAPIs } = useCallAPI();
  const [channelGroups, setChannelGroups] = useState<ChannelGroupListDTO[]>([]);
  // ECOPD-5701：畫面預設「啟用中」，必須**明確**帶 ACTIVE —— 後端預設是 all
  //（那是給 registry 等其他消費者的）。不存進 URL，重新整理回到預設。
  const [statusFilter, setStatusFilter] = useState<ChannelGroupStatusFilter>(
    ChannelGroupStatusFilter.ACTIVE
  );

  // 改過渠道的子頁面要主動叫這支重抓。
  //
  // detail / edit 都是這個元件內部的 `Switch` 分支，元件本身不會 unmount，
  // 所以列表的資料只在掛載時與 `statusFilter` 改變時抓 —— 從詳情頁停用完走回列表，
  // 看到的還是進來時那份舊 DTO，那一列的狀態欄與停用理由都還是停用前的樣子，
  // 像是沒生效。
  // 而這張列表刻意讀 DTO 而非 registry（見 `ChannelGroupListModel` 的註解），
  // 所以 manager 那邊更新 registry 也救不到它。
  //
  // ⚠️ 之後若新增別的會改到渠道的動作，**記得也要接這支** ——
  // 漏接不會有任何錯誤，只會讓列表安靜地顯示舊資料。
  //
  // 也**不要**改回「切回列表路由就重抓」：那會在每次進詳情頁多打一支沒人要的請求。
  // `ChannelGroupListRefresh.test.tsx` 反向釘住了這件事。
  const reloadChannelGroups = useCallback(() => {
    callAPIs(
      [() => channelGroupManager.getChannelGroups(undefined, statusFilter)],
      setChannelGroups
    );
  }, [callAPIs, statusFilter]);

  useEffect(() => {
    reloadChannelGroups();
  }, [reloadChannelGroups]);

  const renderEditForm = useCallback(() => {
    return (
      <ChannelGroupForm
        channelGroups={channelGroups}
        useModel={useChannelGroupEditFormModel}
        onChannelGroupChanged={reloadChannelGroups}
      />
    );
  }, [channelGroups, reloadChannelGroups]);

  const renderDetail = useCallback(() => {
    return <ChannelGroupDetail onChannelGroupChanged={reloadChannelGroups} />;
  }, [reloadChannelGroups]);

  const renderList = useCallback(() => {
    return (
      <ChannelGroupList
        channelGroups={channelGroups}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />
    );
  }, [channelGroups, statusFilter]);

  return (
    <>
      {loading && <LoadingIndicator />}
      <Switch>
        <Route
          exact
          path={'/system/channel-groups/:id/edit'}
          render={renderEditForm}
        />
        <Route
          exact
          path={'/system/channel-groups/:id'}
          render={renderDetail}
        />
        <Route render={renderList} />
      </Switch>
    </>
  );
};
